import { _MiscMessages, ConfigSystemParams, DashboardProfit } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardService } from 'src/app/core/services/utelize/general/dashboard-service';
import { DashboardStatistic } from 'src/app/core/interfaces/general/dashboard-statistic';
import { DashboardTotal_Previous_Current_PercentDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-total-costs-profit',
  templateUrl: './widget-row-total-costs-profit.component.html',
  styleUrls: ['./widget-row-total-costs-profit.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowTotalCostsProfitComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTotalUsage     : boolean | undefined=true;
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
    
  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();
    
  get pollingWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_TotalCostsProfit, false); }
    
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_TotalCostsProfit, 4); }

  private filterCompanyID                 : number | undefined;
  private filterDate                      : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleTotalCostsUsageCustomer: string="Usage customer costs";
  private statTitleTotalProfitUsage       : string="Usage profit";
  private statTitleTotalPurchaseCostsUsage: string="Usage purchase costs";
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number } }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<DashboardStatistic>;
  public visibleContainer: boolean=true;

  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private dashboardService   : DashboardService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();

      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statTotalCostsUsage: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalCostsUsageCustomer,
        value         : undefined,
      }
                   
      let statTotalProfitUsage: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalProfitUsage,
        value         : undefined,
      }
                   
      let statTotalPurchaseCostsUsage: DashboardStatistic={
        decimalPlaces : 2,
        icon          : "bx-pound",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        prefix        : "£",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        subTitle      : "During month",
        title         : this.statTitleTotalPurchaseCostsUsage,
        value         : undefined,
      }

      this.statData.push(statTotalPurchaseCostsUsage);
      this.statData.push(statTotalCostsUsage);
      this.statData.push(statTotalProfitUsage);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;

          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
          
          const totalCostsUsageSource=this.pollingWidgets
                                      ? this.dashboardService.getTotalCostsUsageUpdates(companyId, year, month, day)
                                      : this.dashboardService.getTotalCostsUsage       (companyId, year, month, day);
          
          const totalProfitUsageSource=this.pollingWidgets
                                       ? this.dashboardService.getTotalProfitUsageUpdates(companyId, year, month, day)
                                       : this.dashboardService.getTotalProfitUsage       (companyId, year, month, day);
          
          const totalPurchaseCostsUsageSource=this.pollingWidgets
                                              ? this.dashboardService.getTotalPurchaseCostsUsageUpdates(companyId, year, month, day)
                                              : this.dashboardService.getTotalPurchaseCostsUsage       (companyId, year, month, day);
          
          return combineLatest({
            totalCostsUsage        : safeStart(totalCostsUsageSource,         DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            totalProfitUsage       : safeStart(totalProfitUsageSource,        DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            totalPurchaseCostsUsage: safeStart(totalPurchaseCostsUsageSource, DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20251208 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$) 
      )
      .subscribe(result => {
        this.applyTotalCostsUsage        (result.totalCostsUsage);
        this.applyTotalProfitUsage       (result.totalProfitUsage);
        this.applyTotalPurchaseCostsUsage(result.totalPurchaseCostsUsage);
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalCostsUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalCostsUsageCustomer) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 2))>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalProfitUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalProfitUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 2))>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalPurchaseCostsUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleTotalPurchaseCostsUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=Number((Number(data.currentTotal) || 0).toFixed(stat.decimalPlaces ?? 2))>0 ? "success" : "danger";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
